import { Request, Response } from 'express';
import { injectable, inject } from 'inversify';
import { DomainApplicationService } from '../../../../application/services/DomainApplicationService';
import { CreateDomainRequestSchema, UpdateDomainRequestSchema, GetDomainsQuerySchema } from '../dto/DomainRequestDTOs';
import { validateRequest, validateDomainParams, createErrorResponse } from '../middleware/ValidationMiddleware';
import { 
  createSuccessResponse, 
  createErrorResponse as createStandardErrorResponse, 
  BUSINESS_ACTIONS, 
  DOMAIN_NAMES 
} from '@swoft/core';

/**
 * Domain Controller
 * 
 * RESTful API controller for domain operations.
 * Binds ONLY to DomainApplicationService - no direct domain access.
 * 
 * Enforces Clean Architecture by maintaining strict API boundaries.
 */
@injectable()
export class DomainController {
    constructor(
        @inject('DomainApplicationService')
        private readonly domainApplicationService: DomainApplicationService
    ) {}

    /**
     * Create a new domain
     * POST /domains
     */
    async createDomain(req: Request, res: Response): Promise<void> {
        try {
            // Validate request body
            const validatedRequest = CreateDomainRequestSchema.parse(req.body);
            
            const result = await this.domainApplicationService.createDomain(validatedRequest);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.CREATE_DOMAIN,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.status(201).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_CREATE_FAILED',
                'Failed to create domain',
                BUSINESS_ACTIONS.DDD.CREATE_DOMAIN,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get all domains with optional filtering
     * GET /domains
     */
    async getDomains(req: Request, res: Response): Promise<void> {
        try {
            // Validate query parameters
            const validatedQuery = GetDomainsQuerySchema.parse(req.query);
            
            const result = await this.domainApplicationService.getDomains(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    domains: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_DOMAINS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_LIST_FAILED',
                'Failed to retrieve domains',
                BUSINESS_ACTIONS.DDD.LIST_DOMAINS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get a specific domain by ID
     * GET /domains/:domainId
     */
    async getDomain(req: Request, res: Response): Promise<void> {
        try {
            const { domainId } = req.params;
            
            const result = await this.domainApplicationService.getDomain(domainId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.LIST_DOMAINS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_GET_FAILED',
                'Failed to retrieve domain',
                BUSINESS_ACTIONS.DDD.LIST_DOMAINS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Update an existing domain
     * PUT /domains/:domainId
     */
    async updateDomain(req: Request, res: Response): Promise<void> {
        try {
            const { domainId } = req.params;
            
            // Validate request body
            const validatedRequest = UpdateDomainRequestSchema.parse(req.body);
            
            const result = await this.domainApplicationService.updateDomain(domainId, validatedRequest);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.CREATE_DOMAIN,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_UPDATE_FAILED',
                'Failed to update domain',
                BUSINESS_ACTIONS.DDD.CREATE_DOMAIN,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get domain statistics
     * GET /domains/stats
     */
    async getDomainStats(req: Request, res: Response): Promise<void> {
        try {
            const result = await this.domainApplicationService.getDomainStats();
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.GET_DOMAIN_STATS,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_STATS_FAILED',
                'Failed to retrieve domain statistics',
                BUSINESS_ACTIONS.DDD.GET_DOMAIN_STATS,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(500).json(errorResponse);
        }
    }

    /**
     * Get middleware for domain parameter validation
     */
    static getValidationMiddleware(): any {
        return {
            validateDomainParams,
            validateCreateDomain: validateRequest({ body: CreateDomainRequestSchema }),
            validateUpdateDomain: validateRequest({ body: UpdateDomainRequestSchema }),
            validateGetDomainsQuery: validateRequest({ query: GetDomainsQuerySchema })
        };
    }
}